import { ValidationRuleId } from '@/lib/graphql/generated/graphql';
import { effectiveValidationErrorsAtom } from '@/states/effective-validation-errors.state';
import { productsState } from '@/states/products.state';
import { atom } from 'jotai';

export type BlockingValidationError = {
  ruleId: ValidationRuleId;
  count: number;
  itemIds: string[];
};

/**
 * Rules rendered by a bespoke AlertSidebar section, excluded from the generic
 * per-rule cards so they are not shown twice.
 *
 * `GtinValidness` is warning-severity and only ever mattered once the generic
 * cards started covering warnings too; it has had its own "invalid GTIN"
 * section (with a bulk-fix action) all along.
 */
export const RULES_WITH_DEDICATED_SECTION = new Set<ValidationRuleId>([
  ValidationRuleId.DuplicateVariant,
  ValidationRuleId.GtinValidness,
  ValidationRuleId.PsychologicalPriceApplication,
  ValidationRuleId.RequiredCategory,
  ValidationRuleId.RequiredShippingFees,
]);

/**
 * Warning-severity rules surfaced as a generic card in the AlertSidebar's
 * "À vérifier" group.
 *
 * Deliberately a whitelist, not "every warning rule": the group is a short,
 * curated list of checks worth a human pass before closing the offer, alongside
 * the sections that have their own card (psychological prices, invalid GTINs).
 * A new backend warning rule therefore stays out of the sidebar until it is
 * explicitly added here — and to `toCheckAnomalyTypesCountAtom` through this
 * same set, so the banner counter never drifts from the rendered sections.
 */
export const TO_CHECK_RULES_WITH_CARD = new Set<ValidationRuleId>([
  ValidationRuleId.PositiveStock,
  ValidationRuleId.VatCoherence,
]);

/**
 * Critical validation errors (severity 'error') grouped by rule, over selected
 * deal items only. Drives the "blocking errors" section of the AlertSidebar so
 * Choose users can understand and act on what blocks closing the offer.
 *
 * `required_fields` is a single backend rule: it is kept whole, consistent with
 * the table's per-rule filter and its count.
 */
type ProductsList = ReturnType<typeof productsState.read>;
type EffectiveErrors = ReturnType<typeof effectiveValidationErrorsAtom.read>;

const groupErrorsBySeverity = (
  products: ProductsList,
  effectiveErrors: EffectiveErrors,
  severity: 'error' | 'warning'
): BlockingValidationError[] => {
  const grouped = new Map<ValidationRuleId, string[]>();

  for (const product of products) {
    if (!product.activeVersion.isSelected) continue;
    const errors = effectiveErrors.get(product.id);
    if (errors == null) continue;
    for (const error of errors) {
      if (error.severity !== severity) continue;

      const ids = grouped.get(error.ruleId);
      if (ids) {
        if (!ids.includes(product.id)) ids.push(product.id);
      } else {
        grouped.set(error.ruleId, [product.id]);
      }
    }
  }

  return [...grouped.entries()]
    .map(([ruleId, itemIds]) => ({
      ruleId,
      count: itemIds.length,
      itemIds,
    }))
    .sort((a, b) => a.ruleId.localeCompare(b.ruleId));
};

export const blockingValidationErrorsAtom = atom<BlockingValidationError[]>(
  (get) =>
    groupErrorsBySeverity(
      get(productsState),
      get(effectiveValidationErrorsAtom),
      'error'
    )
);

/**
 * Non-blocking validation errors (severity 'warning') grouped by rule, over
 * selected deal items only. Drives the "to check" section of the AlertSidebar.
 *
 * These are suspicions, not verdicts: a rule like `vat_coherence` is an LLM
 * judgment that can be wrong just as the human categorization can, so it is
 * surfaced for review without preventing the offer from being closed.
 */
export const toCheckValidationErrorsAtom = atom<BlockingValidationError[]>(
  (get) =>
    groupErrorsBySeverity(
      get(productsState),
      get(effectiveValidationErrorsAtom),
      'warning'
    )
);

/**
 * Distinct raw (French) field labels that are missing across selected items
 * flagged with the `required_fields` rule. Parsed from the backend message
 * (`Champs obligatoires manquants: …`), deduplicated and sorted. Powers the
 * "Données manquantes" card description in the AlertSidebar; translate for
 * display with `translateFieldNames`.
 */
export const missingRequiredFieldLabelsAtom = atom<string[]>((get) => {
  const products = get(productsState);
  const effectiveErrors = get(effectiveValidationErrorsAtom);

  const labels = new Set<string>();
  for (const product of products) {
    if (!product.activeVersion.isSelected) continue;
    const errors = effectiveErrors.get(product.id);
    if (errors == null) continue;
    for (const error of errors) {
      if (error.ruleId !== ValidationRuleId.RequiredFields) continue;
      const match = error.message.match(/manquants:\s*(.+)$/);
      if (!match) continue;
      for (const field of match[1].split(',')) {
        const label = field.trim();
        if (label) labels.add(label);
      }
    }
  }

  return [...labels].sort((a, b) => a.localeCompare(b));
});
